<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="title"
    :style="{ width: '90vw', maxWidth: '1200px', height: '90vh' }"
    class="image-mask-editor-dialog"
    @hide="handleClose"
  >
    <div class="mask-editor-container">
      <!-- 工具栏 -->
      <div class="toolbar mb-4 flex items-center gap-4 flex-wrap">
        <!-- 工具选择 -->
        <div class="tool-group flex items-center gap-2">
          <Button
            :icon="tool === 'brush' ? 'pi pi-check' : ''"
            label="画笔"
            size="small"
            :severity="tool === 'brush' ? 'primary' : 'secondary'"
            @click="tool = 'brush'"
          />
          <Button
            :icon="tool === 'eraser' ? 'pi pi-check' : ''"
            label="橡皮擦"
            size="small"
            :severity="tool === 'eraser' ? 'primary' : 'secondary'"
            @click="tool = 'eraser'"
          />
        </div>

        <!-- 画笔大小 -->
        <div class="brush-size-group flex items-center gap-2">
          <label class="text-sm font-medium">画笔大小：</label>
          <Slider
            v-model="brushSize"
            :min="5"
            :max="100"
            :step="5"
            class="w-32"
          />
          <span class="text-sm">{{ brushSize }}px</span>
        </div>

        <!-- 遮罩模式选择 -->
        <!-- <div class="mask-mode-group flex items-center gap-2">
          <label class="text-sm font-medium">模式：</label>
          <Button
            :icon="maskMode === 'blackwhite' ? 'pi pi-check' : ''"
            label="黑白"
            size="small"
            :severity="maskMode === 'blackwhite' ? 'primary' : 'secondary'"
            @click="maskMode = 'blackwhite'"
          />
          <Button
            :icon="maskMode === 'transparent' ? 'pi pi-check' : ''"
            label="挖空透明"
            size="small"
            :severity="maskMode === 'transparent' ? 'primary' : 'secondary'"
            @click="maskMode = 'transparent'"
          />
        </div> -->

        <!-- 操作按钮 -->
        <div class="action-group flex items-center gap-2">
          <Button
            icon="pi pi-refresh"
            label="清空"
            size="small"
            severity="warning"
            @click="clearMask"
          />
          <Button
            icon="pi pi-undo"
            label="撤销"
            size="small"
            :disabled="historyIndex <= 0"
            @click="undo"
          />
          <Button
            icon="pi pi-redo"
            label="重做"
            size="small"
            :disabled="historyIndex >= history.length - 1"
            @click="redo"
          />
        </div>
      </div>

      <!-- 画布容器 -->
      <div class="canvas-container" @wheel.prevent="handleWheel">
        <div
          class="canvas-wrapper"
          :style="{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center'
          }"
        >
          <!-- 原图 -->
          <canvas
            ref="imageCanvasRef"
            class="image-canvas"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseLeave"
          ></canvas>
          <!-- 遮罩层 -->
          <canvas
            ref="maskCanvasRef"
            class="mask-canvas"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseLeave"
            @mouseenter="handleMouseEnter"
          ></canvas>
          <!-- 圆形画笔指示器 -->
          <div
            v-if="showBrushCursor"
            class="brush-cursor"
            :style="{
              left: cursorX + 'px',
              top: cursorY + 'px',
              width: brushSize + 'px',
              height: brushSize + 'px'
            }"
          ></div>
        </div>
      </div>

      <!-- 缩放控制 -->
      <div class="zoom-controls mt-4 flex items-center justify-center gap-2">
        <Button
          icon="pi pi-minus"
          size="small"
          @click="zoom = Math.max(0.1, zoom - 0.1)"
        />
        <span class="text-sm">{{ Math.round(zoom * 100) }}%</span>
        <Button
          icon="pi pi-plus"
          size="small"
          @click="zoom = Math.min(3, zoom + 0.1)"
        />
        <Button
          icon="pi pi-refresh"
          size="small"
          label="重置缩放"
          @click="zoom = 1"
        />
      </div>
    </div>

    <template #footer>
      <Button
        :label="cancelLabel"
        severity="secondary"
        outlined
        @click="handleCancel"
      />
      <Button :label="confirmLabel" @click="handleConfirm" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Slider from 'primevue/slider'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

interface Props {
  modelValue: boolean
  imageUrl: string
  title?: string
  cancelLabel?: string
  confirmLabel?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', maskBlob: Blob): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: '遮罩编辑',
  cancelLabel: '取消',
  confirmLabel: '确认'
})

const emit = defineEmits<Emits>()

// 响应式数据
const imageCanvasRef = ref<HTMLCanvasElement>()
const maskCanvasRef = ref<HTMLCanvasElement>()
const tool = ref<'brush' | 'eraser'>('brush')
const brushSize = ref(20)
const isDrawing = ref(false)
const zoom = ref(1)
const maskMode = ref<'blackwhite' | 'transparent'>('transparent') // 默认挖空透明模式

// 圆形画笔光标状态
const showBrushCursor = ref(false)
const cursorX = ref(0)
const cursorY = ref(0)

// 记录上一个绘制点位置（用于连续绘制）
const lastX = ref(0)
const lastY = ref(0)

// 历史记录
const history = ref<ImageData[]>([])
const historyIndex = ref(-1)

// 计算属性
const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 初始化画布
const initCanvas = async () => {
  if (!imageCanvasRef.value || !maskCanvasRef.value || !props.imageUrl) return

  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = props.imageUrl

  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
  })

  // 设置画布尺寸
  const maxWidth = 800
  const maxHeight = 600
  let width = img.width
  let height = img.height

  // 等比例缩放
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height)
    width = width * ratio
    height = height * ratio
  }

  imageCanvasRef.value.width = width
  imageCanvasRef.value.height = height
  maskCanvasRef.value.width = width
  maskCanvasRef.value.height = height

  // 绘制原图
  const imageCtx = imageCanvasRef.value.getContext('2d')
  if (imageCtx) {
    imageCtx.drawImage(img, 0, 0, width, height)
  }

  // 初始化遮罩层为透明
  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (maskCtx) {
    maskCtx.clearRect(0, 0, width, height)
    // 保存初始状态
    saveHistory()
  }
}

// 保存历史记录
const saveHistory = () => {
  if (!maskCanvasRef.value) return

  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (!maskCtx) return

  // 删除当前位置之后的历史
  history.value = history.value.slice(0, historyIndex.value + 1)

  // 保存当前状态
  const imageData = maskCtx.getImageData(
    0,
    0,
    maskCanvasRef.value.width,
    maskCanvasRef.value.height
  )
  history.value.push(imageData)
  historyIndex.value = history.value.length - 1

  // 限制历史记录数量
  if (history.value.length > 50) {
    history.value.shift()
    historyIndex.value--
  }
}

// 撤销
const undo = () => {
  if (historyIndex.value <= 0 || !maskCanvasRef.value) return

  historyIndex.value--
  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (maskCtx && history.value[historyIndex.value]) {
    maskCtx.putImageData(history.value[historyIndex.value], 0, 0)
  }
}

// 重做
const redo = () => {
  if (historyIndex.value >= history.value.length - 1 || !maskCanvasRef.value)
    return

  historyIndex.value++
  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (maskCtx && history.value[historyIndex.value]) {
    maskCtx.putImageData(history.value[historyIndex.value], 0, 0)
  }
}

// 清空遮罩
const clearMask = () => {
  if (!maskCanvasRef.value) return

  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (maskCtx) {
    maskCtx.clearRect(
      0,
      0,
      maskCanvasRef.value.width,
      maskCanvasRef.value.height
    )
    saveHistory()
  }
}

// 鼠标事件处理
const handleMouseDown = (e: MouseEvent) => {
  if (!maskCanvasRef.value) return

  const rect = maskCanvasRef.value.getBoundingClientRect()
  const x = (e.clientX - rect.left) / zoom.value
  const y = (e.clientY - rect.top) / zoom.value

  // 记录起始点
  lastX.value = x
  lastY.value = y

  isDrawing.value = true
  updateCursorPosition(e)
  draw(e)
}

const handleMouseMove = (e: MouseEvent) => {
  updateCursorPosition(e)
  if (!isDrawing.value) return
  draw(e)
}

const handleMouseUp = () => {
  if (isDrawing.value) {
    isDrawing.value = false
    saveHistory()
  }
}

const handleMouseEnter = () => {
  showBrushCursor.value = true
}

const handleMouseLeave = () => {
  showBrushCursor.value = false
  if (isDrawing.value) {
    isDrawing.value = false
    saveHistory()
  }
}

// 更新圆形光标位置
const updateCursorPosition = (e: MouseEvent) => {
  if (!maskCanvasRef.value) return

  const rect = maskCanvasRef.value.getBoundingClientRect()
  const x = (e.clientX - rect.left) / zoom.value
  const y = (e.clientY - rect.top) / zoom.value

  // 圆形光标应该居中显示，所以减去半径
  cursorX.value = x - brushSize.value / 2
  cursorY.value = y - brushSize.value / 2
}

// 绘制（使用线条连接点，实现平滑连续绘制）
const draw = (e: MouseEvent) => {
  if (!maskCanvasRef.value) return

  const maskCtx = maskCanvasRef.value.getContext('2d')
  if (!maskCtx) return

  const rect = maskCanvasRef.value.getBoundingClientRect()
  const x = (e.clientX - rect.left) / zoom.value
  const y = (e.clientY - rect.top) / zoom.value

  // 设置线条属性
  maskCtx.lineWidth = brushSize.value
  maskCtx.lineCap = 'round'
  maskCtx.lineJoin = 'round'

  if (tool.value === 'brush') {
    // 白色画笔（完全不透明，确保挖空时完全透明）
    maskCtx.strokeStyle = 'rgba(255, 255, 255, 1.0)'
    maskCtx.globalCompositeOperation = 'source-over'
  } else {
    // 橡皮擦
    maskCtx.strokeStyle = 'rgba(0, 0, 0, 1)'
    maskCtx.globalCompositeOperation = 'destination-out'
  }

  // 绘制从上一个点到当前点的线条
  maskCtx.beginPath()
  maskCtx.moveTo(lastX.value, lastY.value)
  maskCtx.lineTo(x, y)
  maskCtx.stroke()

  // 更新上一个点位置
  lastX.value = x
  lastY.value = y

  // 恢复默认混合模式
  if (tool.value === 'eraser') {
    maskCtx.globalCompositeOperation = 'source-over'
  }
}

// 滚轮缩放
const handleWheel = (e: WheelEvent) => {
  e.preventDefault()
  const delta = e.deltaY > 0 ? -0.1 : 0.1
  zoom.value = Math.max(0.1, Math.min(3, zoom.value + delta))
}

// 确认
const handleConfirm = async () => {
  if (!maskCanvasRef.value || !imageCanvasRef.value) return

  const canvas = document.createElement('canvas')
  canvas.width = maskCanvasRef.value.width
  canvas.height = maskCanvasRef.value.height
  const ctx = canvas.getContext('2d')

  if (ctx) {
    if (maskMode.value === 'blackwhite') {
      // 黑白模式：白色遮罩，黑色背景
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(maskCanvasRef.value, 0, 0)
    } else {
      // 挖空透明模式：绘制原图，然后将遮罩区域挖空
      // 先绘制原图
      ctx.drawImage(imageCanvasRef.value, 0, 0)

      // 使用遮罩进行挖空操作
      ctx.globalCompositeOperation = 'destination-out'
      ctx.drawImage(maskCanvasRef.value, 0, 0)
      ctx.globalCompositeOperation = 'source-over'
    }

    // 转换为 blob
    canvas.toBlob((blob) => {
      if (blob) {
        emit('confirm', blob)
        visible.value = false
      }
    }, 'image/png')
  }
}

// 取消
const handleCancel = () => {
  emit('cancel')
  visible.value = false
}

// 关闭
const handleClose = () => {
  emit('cancel')
}

// 监听对话框打开
watch(
  () => props.modelValue,
  async (newValue) => {
    if (newValue && props.imageUrl) {
      await nextTick()
      setTimeout(() => {
        void initCanvas()
      }, 100)
    } else if (!newValue) {
      // 清理
      history.value = []
      historyIndex.value = -1
      zoom.value = 1
      maskMode.value = 'transparent' // 重置为默认模式
    }
  }
)

// 组件卸载时清理
onBeforeUnmount(() => {
  history.value = []
})
</script>

<style scoped>
.image-mask-editor-dialog :deep(.p-dialog-content) {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  height: calc(100% - 120px);
}

.mask-editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar {
  flex-shrink: 0;
}

.canvas-container {
  flex: 1;
  overflow: auto;
  background: #2a2a2a;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.canvas-wrapper {
  position: relative;
  transition: transform 0.2s ease;
}

.image-canvas,
.mask-canvas {
  display: block;
  border: 1px solid #444;
  cursor: none;
}

.image-canvas {
  position: absolute;
  top: 0;
  left: 0;
}

.mask-canvas {
  position: relative;
  opacity: 0.6;
}

.brush-cursor {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  pointer-events: none;
  transform: translate(0, 0);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.5),
    inset 0 0 0 1px rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.zoom-controls {
  flex-shrink: 0;
}
</style>
